'use client';

import { VStack, token } from '@terra/ui-styled-system';
import { formatDistanceToNow } from 'date-fns';
import { useTranslations } from 'next-intl';
import { Badge } from '../../components/badge';
import { Link } from '../../components/styled/link';
import { Tooltip } from '../../components/styled/tooltip';
import { BodyDefault, MetaDefault } from '../../components/typography';

type CredentialLoginHealthStatus =
  | 'HEALTHY'
  | 'NEEDS_ATTENTION'
  | 'CONTRADICTED'
  | 'SHARED_ACCOUNT_SUSPECTED';

type AttentionStatus = Exclude<CredentialLoginHealthStatus, 'HEALTHY'>;

export type CredentialLoginHealthBadgeValue = {
  status: CredentialLoginHealthStatus;
  field?: string | null;
  observedAt?: string | Date | null;
};

export type CredentialLoginHealthBadgeProps = {
  loginHealth?: CredentialLoginHealthBadgeValue | null;
  loginBehaviorHref?: string;
};

const CONTRADICTED_FIELDS = [
  'concurrentSessions',
  'logoutScope',
  'tokenRotation',
] as const;

type ContradictedField = (typeof CONTRADICTED_FIELDS)[number];

const STATUS_BACKGROUND: Record<AttentionStatus, string> = {
  NEEDS_ATTENTION: token('colors.bg.neutral.error'),
  CONTRADICTED: token('colors.bg.neutral.warning'),
  SHARED_ACCOUNT_SUSPECTED: token('colors.bg.neutral.warning'),
};

const STATUS_LABEL_KEY = {
  NEEDS_ATTENTION: 'loginHealth.needsAttention.label',
  CONTRADICTED: 'loginHealth.contradicted.label',
  SHARED_ACCOUNT_SUSPECTED: 'loginHealth.sharedAccountSuspected.label',
} as const satisfies Record<AttentionStatus, string>;

const CONTRADICTED_DESCRIPTION_KEY = {
  concurrentSessions: 'loginHealth.contradicted.concurrentSessions',
  logoutScope: 'loginHealth.contradicted.logoutScope',
  tokenRotation: 'loginHealth.contradicted.tokenRotation',
} as const satisfies Record<ContradictedField, string>;

const isContradictedField = (
  field: string | null | undefined,
): field is ContradictedField =>
  CONTRADICTED_FIELDS.some((candidate) => candidate === field);

const isAttentionStatus = (
  status: CredentialLoginHealthStatus,
): status is AttentionStatus => status !== 'HEALTHY';

export function CredentialLoginHealthBadge({
  loginHealth,
  loginBehaviorHref,
}: CredentialLoginHealthBadgeProps) {
  const t = useTranslations('designSystem.applicationCredentials');

  if (!loginHealth || !isAttentionStatus(loginHealth.status)) {
    return null;
  }

  const { status, field, observedAt } = loginHealth;

  const description = (() => {
    if (status === 'NEEDS_ATTENTION') {
      return t('loginHealth.needsAttention.description');
    }

    if (status === 'SHARED_ACCOUNT_SUSPECTED') {
      return t('loginHealth.sharedAccountSuspected.description');
    }

    return isContradictedField(field)
      ? t(CONTRADICTED_DESCRIPTION_KEY[field])
      : t('loginHealth.contradicted.description');
  })();

  const showLoginBehaviorLink =
    !!loginBehaviorHref && status !== 'NEEDS_ATTENTION';

  return (
    <Tooltip
      maxW="320px"
      interactive
      customContent={
        <VStack gap={1.5} alignItems="flex-start">
          <BodyDefault color="fg.tooltip.default">{description}</BodyDefault>
          {observedAt ? (
            <MetaDefault color="fg.tooltip.muted">
              {formatDistanceToNow(new Date(observedAt), { addSuffix: false })}{' '}
              {t('ago')}
            </MetaDefault>
          ) : null}
          {showLoginBehaviorLink ? (
            <Link href={loginBehaviorHref} fontSize="xs">
              {t('loginHealth.reviewLoginBehavior')}
            </Link>
          ) : null}
        </VStack>
      }
    >
      <Badge
        variant="fill"
        size="sm"
        bg={STATUS_BACKGROUND[status]}
        flexShrink={0}
      >
        {t(STATUS_LABEL_KEY[status])}
      </Badge>
    </Tooltip>
  );
}
